import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { EmployerFormFields } from 'src/@types';
import { Box, Grid, GridItem, Heading } from '@chakra-ui/react';
import { DynamicInput } from 'src/components/shared/form-elements';
import { validationMessages } from 'src/components/shared/validation/validation-messages';
import { employerStatementFormFields } from '../config';

export const EmployerStatementEmploymentSection = ({
  heading,
  register,
  values,
  errors
}: {
  heading: string;
  register: UseFormRegister<EmployerFormFields>;
  values: EmployerFormFields;
  errors: FieldErrors<EmployerFormFields>;
}) => {
  return (
    <>
      <Heading size={`md`}>{heading}</Heading>
      <Grid
        templateColumns={{
          base: 'repeat(1, 1fr)'
        }}
        gap={4}>
        <GridItem colSpan={1}>
          <DynamicInput
            type={employerStatementFormFields.werknemersarbeidsvorm.type}
            validation={{ required: validationMessages.required }}
            id={employerStatementFormFields.werknemersarbeidsvorm.fieldName}
            defaultValue={values.werknemersarbeidsvorm}
            register={register}
            options={[
              {
                label:
                  'Een arbeidsovereenkomst voor onbepaalde tijd of is aangesteld in vaste dienst',
                value: 'Onbepaalde tijd'
              },
              {
                label:
                  'Een arbeidsovereenkomst voor bepaalde tijd of is aangesteld in tijdelijke dienst',
                value: 'Bepaalde tijd'
              },
              {
                label:
                  'Inkomen uit een flexibele arbeidsrelatie (bijv. uitzend-, inval-, of oproepkracht (inclusief fase)), nl:',
                value: 'Flexibel'
              }
            ]}
            label={employerStatementFormFields.werknemersarbeidsvorm.label}
          />
        </GridItem>

        {values.werknemersarbeidsvorm === 'Bepaalde tijd' && (
          <GridItem colSpan={1}>
            <DynamicInput
              id={employerStatementFormFields.werknemersuitdiensttreding.fieldName}
              label={employerStatementFormFields.werknemersuitdiensttreding.label}
              type={employerStatementFormFields.werknemersuitdiensttreding.type}
              validation={{ required: validationMessages.required }}
              tooltip={employerStatementFormFields.werknemersuitdiensttreding.tooltip}
              error={errors.werknemersuitdiensttreding}
              optionalStyle={true}
              register={register}
            />
          </GridItem>
        )}

        {values.werknemersarbeidsvorm === 'Flexibel' && (
          <GridItem colSpan={1}>
            <DynamicInput
              id={employerStatementFormFields.werknemersflexibelarbeidsvorm.fieldName}
              label={employerStatementFormFields.werknemersflexibelarbeidsvorm.label}
              type={employerStatementFormFields.werknemersflexibelarbeidsvorm.type}
              validation={{ required: validationMessages.required }}
              tooltip={employerStatementFormFields.werknemersflexibelarbeidsvorm.tooltip}
              error={errors.werknemersflexibelarbeidsvorm}
              hint={employerStatementFormFields.werknemersflexibelarbeidsvorm.hint}
              optionalStyle={true}
              register={register}
            />
          </GridItem>
        )}

        <GridItem colSpan={1}>
          <DynamicInput
            id={employerStatementFormFields.heeftproeftijd.fieldName}
            label={employerStatementFormFields.heeftproeftijd.label}
            type={employerStatementFormFields.heeftproeftijd.type}
            validation={{ required: validationMessages.required }}
            defaultValue={values.heeftproeftijd}
            register={register}
            options={[
              { label: 'Ja', value: 'true' },
              { label: 'Nee', value: 'false' }
            ]}
          />
        </GridItem>

        {values.heeftproeftijd === 'true' && (
          <GridItem colSpan={1}>
            <DynamicInput
              id={employerStatementFormFields.proeftijdverstreken.fieldName}
              label={employerStatementFormFields.proeftijdverstreken.label}
              type={employerStatementFormFields.proeftijdverstreken.type}
              validation={{ required: validationMessages.required }}
              defaultValue={values.proeftijdverstreken}
              register={register}
              optionalStyle={true}
              options={[
                { label: 'Ja', value: 'true' },
                { label: 'Nee', value: 'false' }
              ]}
            />
          </GridItem>
        )}
        <GridItem colSpan={1}>
          <DynamicInput
            id={employerStatementFormFields.dienstverbandbinnenkortbeeindigen.fieldName}
            label={employerStatementFormFields.dienstverbandbinnenkortbeeindigen.label}
            type={employerStatementFormFields.dienstverbandbinnenkortbeeindigen.type}
            validation={{ required: validationMessages.required }}
            defaultValue={values.dienstverbandbinnenkortbeeindigen}
            register={register}
            options={[
              { label: 'Ja', value: 'true' },
              { label: 'Nee', value: 'false' }
            ]}
          />
        </GridItem>
        {values.dienstverbandbinnenkortbeeindigen === 'true' && (
          <GridItem colSpan={1}>
            <DynamicInput
              id={employerStatementFormFields.toelichtingDienstverbandBeeindiging.fieldName}
              label={employerStatementFormFields.toelichtingDienstverbandBeeindiging.label}
              type={employerStatementFormFields.toelichtingDienstverbandBeeindiging.type}
              validation={{
                required: validationMessages.required,
                maxLength: { value: 350, message: validationMessages.maxLength(350) }
              }}
              values={values}
              tooltip={employerStatementFormFields.toelichtingDienstverbandBeeindiging.tooltip}
              error={errors.toelichtingDienstverbandBeeindiging}
              hint={employerStatementFormFields.toelichtingDienstverbandBeeindiging.hint}
              optionalStyle={true}
              register={register}
            />
          </GridItem>
        )}
        <GridItem colSpan={1}>
          <DynamicInput
            id={employerStatementFormFields.isDirecteurAandeelhouder.fieldName}
            label={employerStatementFormFields.isDirecteurAandeelhouder.label}
            type={employerStatementFormFields.isDirecteurAandeelhouder.type}
            validation={{ required: validationMessages.required }}
            tooltip={employerStatementFormFields.isDirecteurAandeelhouder.tooltip}
            error={errors.isDirecteurAandeelhouder}
            defaultValue={values.isDirecteurAandeelhouder}
            register={register}
            options={[
              { label: 'Ja', value: 'true' },
              { label: 'Nee', value: 'false' }
            ]}
          />
        </GridItem>

        {values.isDirecteurAandeelhouder === 'true' && (
          <DynamicInput
            id={employerStatementFormFields.percentageAandelen.fieldName}
            label={employerStatementFormFields.percentageAandelen.label}
            type={employerStatementFormFields.percentageAandelen.type}
            validation={{
              required: validationMessages.required,
              min: { value: 0, message: validationMessages.min(0) },
              max: { value: 100, message: validationMessages.max(100) }
            }}
            tooltip={employerStatementFormFields.percentageAandelen.tooltip}
            error={errors.percentageAandelen}
            optionalStyle={true}
            register={register}
          />
        )}
        {values.werknemersarbeidsvorm === 'Bepaalde tijd' && (
          <Box>
            <DynamicInput
              id={employerStatementFormFields.verklaringVoortzettingDienstverband.fieldName}
              label={employerStatementFormFields.verklaringVoortzettingDienstverband.label}
              type={employerStatementFormFields.verklaringVoortzettingDienstverband.type}
              validation={{ required: validationMessages.required }}
              tooltip={employerStatementFormFields.verklaringVoortzettingDienstverband.tooltip}
              error={errors.verklaringVoortzettingDienstverband}
              defaultValue={values.verklaringVoortzettingDienstverband}
              register={register}
              options={[
                {
                  label: 'Ja, voor bepaalde tijd voor een duur van minimaal',
                  value: 'jaBepaaldeTijd'
                },
                { label: 'Ja, voor onbepaalde tijd', value: 'jaOnbepaaldeTijd' },
                { label: 'Nee, geen voortgezet of vernieuwd dienstverband', value: 'nee' }
              ]}
            />
            {values.verklaringVoortzettingDienstverband === 'jaBepaaldeTijd' && (
              <DynamicInput
                id={employerStatementFormFields.jaBepaaldeTijd.fieldName}
                label={employerStatementFormFields.jaBepaaldeTijd.label}
                type={employerStatementFormFields.jaBepaaldeTijd.type}
                validation={{ required: validationMessages.required }}
                tooltip={employerStatementFormFields.jaBepaaldeTijd.tooltip}
                error={errors.jaBepaaldeTijd}
                optionalStyle={true}
                register={register}></DynamicInput>
            )}

            {values.verklaringVoortzettingDienstverband === 'jaOnbepaaldeTijd' && (
              <DynamicInput
                id={employerStatementFormFields.wordenDeArbeidsvoorwaardenGewijzigd.fieldName}
                label={employerStatementFormFields.wordenDeArbeidsvoorwaardenGewijzigd.label}
                type={employerStatementFormFields.wordenDeArbeidsvoorwaardenGewijzigd.type}
                validation={{ required: validationMessages.required }}
                tooltip={employerStatementFormFields.wordenDeArbeidsvoorwaardenGewijzigd.tooltip}
                error={errors.wordenDeArbeidsvoorwaardenGewijzigd}
                defaultValue={values.wordenDeArbeidsvoorwaardenGewijzigd}
                optionalStyle={true}
                register={register}
                options={[
                  {
                    value: 'true',
                    label: 'Ja'
                  },
                  {
                    value: 'false',
                    label: 'Nee'
                  }
                ]}
              />
            )}
            {values.wordenDeArbeidsvoorwaardenGewijzigd === 'true' &&
              values.verklaringVoortzettingDienstverband === 'jaOnbepaaldeTijd' && (
                <DynamicInput
                  id={
                    employerStatementFormFields.toelichtingwordenDeArbeidsvoorwaardenGewijzigd
                      .fieldName
                  }
                  label={
                    employerStatementFormFields.toelichtingwordenDeArbeidsvoorwaardenGewijzigd.label
                  }
                  type={
                    employerStatementFormFields.toelichtingwordenDeArbeidsvoorwaardenGewijzigd.type
                  }
                  validation={{
                    required: validationMessages.required,
                    maxLength: { value: 350, message: validationMessages.maxLength(350) }
                  }}
                  tooltip={
                    employerStatementFormFields.toelichtingwordenDeArbeidsvoorwaardenGewijzigd
                      .tooltip
                  }
                  error={errors.toelichtingwordenDeArbeidsvoorwaardenGewijzigd}
                  hint={
                    employerStatementFormFields.toelichtingwordenDeArbeidsvoorwaardenGewijzigd.hint
                  }
                  optionalStyle={true}
                  register={register}
                />
              )}

            <DynamicInput
              id={employerStatementFormFields.verklaringvoortzettingdienstverbandnaam.fieldName}
              label={employerStatementFormFields.verklaringvoortzettingdienstverbandnaam.label}
              type={employerStatementFormFields.verklaringvoortzettingdienstverbandnaam.type}
              validation={{ required: validationMessages.required }}
              tooltip={employerStatementFormFields.verklaringvoortzettingdienstverbandnaam.tooltip}
              error={errors.verklaringvoortzettingdienstverbandnaam}
              hint={employerStatementFormFields.verklaringvoortzettingdienstverbandnaam.hint}
              register={register}
            />
          </Box>
        )}
      </Grid>
    </>
  );
};
